import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Download, FileText, Database, X } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  convertToCSV,
  convertToJSON,
  downloadFile,
  fetchCollectionData,
} from '../../../utils/fileDownloadUtils';

const ExportDataAction = () => {
  const [showModal, setShowModal] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv');
  const [selectedCollections, setSelectedCollections] = useState(new Set());
  const [isExporting, setIsExporting] = useState(false);

  const collections = [
    'admin',
    'alumni',
    'alumniReports',
    'bif_applications',
    'clients',
    'coaches',
    'companyClient',
    'companyProfiles',
    'documentBank',
    'entrepreneurProfiles',
    'legal_consents',
    'moderator',
    'moderatorReports',
    'organizations',
    'otps',
    'private_chats',
    'programRatings',
    'programs',
    'scalingModulesProgress',
    'teamMembers',
    'threads',
    'userActivity',
    'users',
    'weeklyReports',
  ];

  const handleExport = async () => {
    if (selectedCollections.size === 0) {
      toast.error('Please select at least one collection to export');
      return;
    }

    setIsExporting(true);
    try {
      const allData = [];
      const collectionNames = Array.from(selectedCollections);

      for (const collectionName of collectionNames) {
        const collectionData = await fetchCollectionData(collectionName);
        const dataWithCollection = collectionData.map(record => ({
          ...record,
          _collection: collectionName,
        }));
        allData.push(...dataWithCollection);
      }

      if (allData.length === 0) {
        toast.warning('No data found in selected collections');
        return;
      }

      let fileContent, filename, mimeType;

      if (exportFormat === 'csv') {
        fileContent = convertToCSV(allData);
        filename = `exported_data_${new Date().toISOString().split('T')[0]}.csv`;
        mimeType = 'text/csv';
      } else {
        fileContent = convertToJSON(allData, true);
        filename = `exported_data_${new Date().toISOString().split('T')[0]}.json`;
        mimeType = 'application/json';
      }

      downloadFile(fileContent, filename, mimeType);

      const collectionsList = collectionNames.join(', ');
      toast.success(
        `Successfully exported ${allData.length} records from ${collectionsList} in ${exportFormat.toUpperCase()} format`
      );

      setShowModal(false);
      setSelectedCollections(new Set());
    } catch (error) {
      toast.error('Failed to export data: ' + error.message);
    } finally {
      setIsExporting(false);
    }
  };

  const handleCollectionToggle = collection => {
    setSelectedCollections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(collection)) {
        newSet.delete(collection);
      } else {
        newSet.add(collection);
      }
      return newSet;
    });
  };

  return (
    <>
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setShowModal(true)}
        className="flex flex-col items-center rounded-lg bg-gradient-to-br from-blue-50 via-blue-100 to-blue-200 p-3 shadow-md transition-colors hover:bg-blue-200 md:p-4"
      >
        <span className="mb-1.5 md:mb-2">
          <Download className="h-5 w-5 text-blue-700 md:h-6 md:w-6" />
        </span>
        <span className="text-xs font-medium text-blue-900 md:text-sm">Export Data</span>
      </motion.button>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-2xl rounded-xl bg-white p-6 shadow-2xl"
          >
            <div className="mb-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-blue-100 p-2">
                  <Download className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">Export Data</h3>
                  <p className="text-sm text-gray-600">Select collections and format for export</p>
                </div>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Export Format
                </label>
                <div className="flex gap-4">
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="radio"
                      value="csv"
                      checked={exportFormat === 'csv'}
                      onChange={e => setExportFormat(e.target.value)}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <FileText className="h-4 w-4 text-gray-600" />
                    <span className="text-sm text-gray-700">CSV</span>
                  </label>
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="radio"
                      value="json"
                      checked={exportFormat === 'json'}
                      onChange={e => setExportFormat(e.target.value)}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <Database className="h-4 w-4 text-gray-600" />
                    <span className="text-sm text-gray-700">JSON</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="mb-3 block text-sm font-medium text-gray-700">
                  Select Collections
                </label>
                <div className="grid max-h-48 grid-cols-2 gap-2 overflow-y-auto rounded-lg border border-gray-200 p-3">
                  {collections.map(collection => (
                    <label key={collection} className="flex cursor-pointer items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedCollections.has(collection)}
                        onChange={() => handleCollectionToggle(collection)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm capitalize text-gray-700">
                        {collection.replace(/([A-Z])/g, ' $1').trim()}
                      </span>
                    </label>
                  ))}
                </div>
                <div className="mt-2 flex gap-2">
                  <button
                    onClick={() => setSelectedCollections(new Set(collections))}
                    className="text-xs text-blue-600 hover:text-blue-700"
                  >
                    Select All
                  </button>
                  <button
                    onClick={() => setSelectedCollections(new Set())}
                    className="text-xs text-gray-600 hover:text-gray-700"
                  >
                    Clear All
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleExport}
                disabled={isExporting || selectedCollections.size === 0}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {isExporting ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    Export Data
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
};

export default ExportDataAction;
